import { useState } from 'react';
import { Menu, MenuItem, ListItemText, CircularProgress, Tooltip } from '@mui/material';
import { Download } from '@mui/icons-material';
import StyledButton from 'components/StyledButton';
import { exportData, EXPORT_FORMATS } from 'state/exports';


/**
 * ExportButton — reusable export menu for any tabular data.
 *
 * Props:
 *   data        {Array}    — rows to export
 *   fileName    {string}   — base file name (timestamp appended automatically)
 *   omitFields  {string[]} — field keys to exclude (default [])
 *   formats     {string[]} — whitelist of format keys; omit to show all
 *                            ('xlsx' | 'csv' | 'pdf' | 'text' | 'html')
 *   onExport    {fn}       — optional callback(format) fired after export
 *   disabled    {bool}
 *   tooltip     {string}   — tooltip text (default 'Export')
 */
const ExportButton = ({
    data,
    fileName = 'Export',
    omitFields = [],
    formats,
    onExport,
    disabled,
    tooltip = 'Export',
}) => {
    const [anchor,    setAnchor]    = useState(null);
    const [exporting, setExporting] = useState(false);

    const available = formats
        ? EXPORT_FORMATS.filter(f => formats.includes(f.key))
        : EXPORT_FORMATS;

    const handleOpen  = (e) => { if (!disabled) setAnchor(e.currentTarget); };
    const handleClose = ()  => setAnchor(null);

    const handleSelect = async (format) => {
        handleClose();
        setExporting(true);
        try {
            await exportData(data, fileName, format, omitFields);
            onExport?.(format);
        } finally {
            setExporting(false);
        }
    };

    return (
        <>
            <Tooltip title={tooltip} arrow>
                <span>
                    <StyledButton disabled={disabled || exporting} onClick={handleOpen}>
                        {exporting ? <CircularProgress size={18} color="inherit" /> : <Download />}
                    </StyledButton>
                </span>
            </Tooltip>

            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={handleClose}
                slotProps={{ paper: { elevation: 3 } }}
            >
                {available.map(({ key, label }) => (
                    <MenuItem key={key} onClick={() => handleSelect(key)} dense>
                        <ListItemText primary={label} />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default ExportButton;
